import type { IntensityTreemapItem } from '../../types/emissions';

// ============================================================================
// Metric Definitions
// ============================================================================

export type IntensityMetricKey = 'floorArea' | 'employees' | 'revenue';

export interface IntensityMetricDef {
  key: IntensityMetricKey;
  label: string;
  unit: string;
  denominatorLabel: string;
}

const INTENSITY_METRICS: IntensityMetricDef[] = [
  { key: 'floorArea', label: 'Floor Area', unit: 'kgCO₂e/m²', denominatorLabel: 'm²' },
  { key: 'employees', label: 'Employees', unit: 'kgCO₂e/employee', denominatorLabel: 'employees' },
  { key: 'revenue', label: 'Revenue', unit: 'kgCO₂e/$M', denominatorLabel: '$M revenue' },
];

// ============================================================================
// Data Extraction
// ============================================================================

/**
 * Extract the denominator value for a given metric from an initiative's business metrics.
 * Returns null if the metric is not available.
 */
export const getDenominator = (item: IntensityTreemapItem, metric: IntensityMetricKey): number | null => {
  const bm = item.businessMetrics;
  if (!bm) return null;

  switch (metric) {
    case 'floorArea':
      return bm.floorArea ?? null;
    case 'employees':
      return bm.employeeCount ?? null;
    case 'revenue':
      // Revenue is stored in currency units; intensity is per million
      return bm.revenue != null && bm.revenue > 0 ? bm.revenue / 1_000_000 : null;
    default:
      return null;
  }
};

// ============================================================================
// Treemap Node
// ============================================================================

export interface TreemapNode {
  name: string;
  /** Box size — the denominator value */
  size: number;
  /** Intensity ratio — drives the heatmap color */
  intensity: number;
  /** Total CO2e in kg */
  totalCo2e: number;
  /** Raw denominator value (for tooltip) */
  denominatorValue: number;
  /** Initiative/entity ID */
  id: string;
}

/**
 * Build treemap data from the API response for a given metric.
 * Only includes items that have a valid (> 0) denominator for the selected metric.
 */
export const buildTreemapData = (
  items: IntensityTreemapItem[],
  metric: IntensityMetricKey,
): TreemapNode[] => {
  const nodes: TreemapNode[] = [];

  for (const item of items) {
    const denom = getDenominator(item, metric);
    if (denom == null || denom <= 0) continue;

    nodes.push({
      name: item.name,
      size: denom,
      intensity: item.totalCo2e / denom,
      totalCo2e: item.totalCo2e,
      denominatorValue: denom,
      id: item._id,
    });
  }

  return nodes;
};

/**
 * Return only metrics that have at least one item with data.
 */
export const getAvailableMetrics = (items: IntensityTreemapItem[]): IntensityMetricDef[] => {
  return INTENSITY_METRICS.filter((metric) =>
    items.some((item) => {
      const d = getDenominator(item, metric.key);
      return d != null && d > 0;
    })
  );
};

// ============================================================================
// Color Interpolation (green → yellow → red)
// ============================================================================

const GREEN = [14, 147, 129] as const;  // emerald-ish
const YELLOW = [234, 179, 8] as const;  // amber
const RED = [220, 53, 69] as const;     // red

const interpolateRgb = (c1: readonly number[], c2: readonly number[], t: number): string => {
  const r = Math.round(c1[0] + (c2[0] - c1[0]) * t);
  const g = Math.round(c1[1] + (c2[1] - c1[1]) * t);
  const b = Math.round(c1[2] + (c2[2] - c1[2]) * t);
  return `rgb(${r}, ${g}, ${b})`;
};

/**
 * Map an intensity value to a green→yellow→red color.
 * `min` gets green, `max` gets red, midpoint gets yellow.
 */
export const getIntensityColor = (intensity: number, min: number, max: number): string => {
  if (max <= min) return interpolateRgb(GREEN, GREEN, 0);
  const ratio = Math.max(0, Math.min(1, (intensity - min) / (max - min)));
  if (ratio < 0.5) {
    return interpolateRgb(GREEN, YELLOW, ratio * 2);
  }
  return interpolateRgb(YELLOW, RED, (ratio - 0.5) * 2);
};
